import React, { useState } from 'react';
import { Button, IconButton, InputAdornment, Stack, Typography } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as Yup from 'yup';
import CustomTextInput from '../../../components/CustomTextInput';
import { authApiClient } from '../../../api/axios';
import { showSnackbar } from '../../../api/snackbarSlice';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { map } from 'lodash';
import appConfig from '../../../config/appConfig';
import { completePostLoginFlow } from '../../../utils/authToken';

export const LoginEmailSchema = Yup.object().shape({
  email: Yup.string().email('Invalid email format').required('Email is required'),
  password: Yup.string().required('Password is required'),
});

const LoginForm = ({ redirectTo }) => {
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
  } = useForm({
    resolver: yupResolver(LoginEmailSchema),
  });

  const onSubmit = async (data) => {
    setLoading(true);
    authApiClient
      .post('/login', data)
      .then(async (res) => {
        if (!res.data.token) {
          const message = res.data.message || 'OTP sent to your email';
          dispatch(
            showSnackbar({
              message,
              severity: 'success',
            }),
          );
          navigate('/login', {
            replace: true,
            state: { email: data.email, stage: 'otp_required', message, redirectTo },
          });

          return;
        }

        const result = await completePostLoginFlow({
          requiresOrgSelection: !!res.data.requires_org_selection,
          email: data.email,
          dispatch,
          navigate,
          redirectTo,
          queryClient,
          onMfaRequired: (payload) => {
            navigate('/login', {
              replace: true,
              state: {
                stage: 'otp_required',
                email: data.email,
                message: payload.message,
                orgMfa: true,
                organizationId: payload.organizationId,
                redirectTo,
              },
            });
          },
        });

        if (result?.error) {
          const message =
            result.error?.response?.data?.errors?.message ||
            result.error?.response?.data?.message ||
            result.error?.message ||
            'Something went wrong!';

          dispatch(
            showSnackbar({
              message,
              severity: 'error',
            }),
          );
        }
      })
      .catch((err) => {
        if (err?.response?.data?.errors) {
          map(err?.response?.data?.errors, (value, key) => {
            setError(key, { message: value, type: 'string' });
          });
        } else if (err?.response?.data?.errors?.message) {
          dispatch(
            showSnackbar({
              message: err?.response?.data?.errors?.message,
              severity: 'warning',
            }),
          );
        } else {
          dispatch(
            showSnackbar({
              message: err?.response?.data?.message || 'Something went wrong!',
              severity: 'warning',
            }),
          );
        }
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <>
      <Stack sx={{ alignItems: 'center', mb: 4 }}>
        <img src={appConfig.brandLogoPath} alt="logo" width="50%" />
      </Stack>
      <Stack spacing={2}>
        <CustomTextInput data-testid="email-input" name="email" label="Email" control={control} errors={errors} />
        <CustomTextInput
          data-testid="password-input"
          name="password"
          type={showPassword ? 'text' : 'password'}
          label="Password"
          control={control}
          errors={errors}
          endAdornment={
            <InputAdornment position="end">
              <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                {showPassword ? <VisibilityOff /> : <Visibility />}
              </IconButton>
            </InputAdornment>
          }
        />
        <Typography variant="body2">
          <Link to="/forgot-password" style={{ textDecoration: 'none' }}>
            Forgot Password?
          </Link>
        </Typography>
        <Button
          data-testid="sign-in-button"
          type="button"
          variant="contained"
          className="submit"
          onClick={handleSubmit(onSubmit)}
        >
          {loading ? 'Logging in...' : 'Sign In'}
        </Button>
        <Typography variant="body2">
          <Link to="/signup" style={{ textDecoration: 'none' }}>
            Don&#39;t have an account? Sign Up
          </Link>
        </Typography>
      </Stack>
    </>
  );
};

export default LoginForm;
